import React from 'react';
import { Button, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { MUITableColumn } from 'components/shared/MUITable';
import { SmartIntakeRow } from './SmartIntakeTypes';
import { confidenceColor } from './SmartIntakeMapper';

interface ActionHandlers {
  onReview: (row: SmartIntakeRow) => void;
  onDismiss: (row: SmartIntakeRow) => void;
  dismissingId: string | null;
}

export const createSmartIntakeTableColumns = ({
  onReview,
  onDismiss,
  dismissingId,
}: ActionHandlers): MUITableColumn<SmartIntakeRow>[] => [
  {
    header: 'Upload Date',
    sortKey: 'uploadDateSortValue',
    renderKey: 'uploadDate',
    cellProps: { sx: { minWidth: 200, width: 200 } },
  },
  {
    header: 'Name',
    sortKey: 'name',
    renderKey: 'name',
    cellProps: { sx: { flex: 1 } },
  },
  {
    header: 'Confidence Score',
    sortKey: 'confidence',
    renderKey: 'confidence',
    cellProps: { sx: { minWidth: 200, width: 200 } },
    renderCell: row => {
      if (row.confidence == null) return <Typography variant="body2">—</Typography>;
      const color = confidenceColor(row.confidenceBand);
      const Icon =
        row.confidenceBand === 'high' ? CheckCircleIcon : row.confidenceBand === 'partial' ? WarningIcon : ErrorIcon;
      const label =
        row.confidenceBand === 'high' ? 'High' : row.confidenceBand === 'partial' ? 'Partial match' : 'Low extraction';
      return (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Icon sx={{ fontSize: 18, color }} />
          <Typography variant="body2" sx={{ color }}>
            {Math.round(row.confidence * 100)}% - {label}
          </Typography>
        </Stack>
      );
    },
  },
  {
    header: 'Status',
    sortKey: 'statusLabel',
    renderKey: 'statusLabel',
    cellProps: { sx: { flex: 1 } },
    renderCell: row => (
      <Typography
        variant="body2"
        color={row.status === 'parsing_in_progress' ? 'text.disabled' : 'text.primary'}
        fontStyle={row.status === 'parsing_in_progress' ? 'italic' : 'normal'}
      >
        {row.statusLabel}
      </Typography>
    ),
  },
  {
    header: 'Uploaded by',
    sortKey: 'uploadedBy',
    renderKey: 'uploadedBy',
    cellProps: { sx: { flex: 1 } },
  },
  {
    header: 'Action',
    renderKey: 'id',
    cellProps: { sx: { minWidth: 90, width: 90 } },
    renderCell: row => {
      if (row.status === 'parsing_in_progress') {
        return (
          <Button size="small" variant="contained" disabled>
            Review
          </Button>
        );
      }
      if (row.status === 'parsing_failed') {
        return (
          <Button size="small" variant="text" disabled={dismissingId === row.id} onClick={() => onDismiss(row)}>
            Dismiss
          </Button>
        );
      }
      return (
        <Button size="small" variant="contained" onClick={() => onReview(row)}>
          Review
        </Button>
      );
    },
  },
];
